import React, { useState, useEffect } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { GlobalDropdownField, DropdownOption } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';

import {
  Student, BatchInfo,
  IdCardFront, IdCardBack,
  formatDate, formatDOB,
  buildPrintHTML,
} from './IdCardComponents';

// Month name → number map
const MONTH_MAP: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4,
  may: 5, june: 6, july: 7, august: 8,
  september: 9, october: 10, november: 11, december: 12,
};

interface Batch {
  batch_id:    string;
  batch_name:  string;
  batch_time:  string;
  end_month:   string; // e.g. "September"
  end_year:    number; // e.g. 2026
  course_id:   string;
  course_name?: string;
  is_active:   boolean;
}

interface ApiResponse {
  success: boolean;
  data: Student[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

const IdCardGenerator: React.FC = () => {
  const { profile, fetchProfile } = useOperatorProfileStore();

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewStudent, setPreviewStudent] = useState<Student | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });

  // Card settings
  const [schoolName, setSchoolName] = useState('School Name');
  const [schoolAddress, setSchoolAddress] = useState('Place, District State – 000000');
  const [websiteUrl, setWebsiteUrl] = useState('hciindia.in');
  const [validTill, setValidTill] = useState('');
  const [branchId, setBranchId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  // Sync profile → settings
  useEffect(() => {
    if (profile) {
      setSchoolName(profile.institute_name);
      // Strip institute name prefix if branch_name contains it, show branch only
      const branch = profile.branch_name
        .replace(profile.institute_name, '')
        .replace(/^[\s\-–—,]+/, '')
        .trim();
      setSchoolAddress(branch || profile.branch_name);
      setBranchId(profile.branch_id);
    }
  }, [profile]);

  // Derive validTill from selected batch end_month/end_year
  useEffect(() => {
    if (batchInfo?.valid_till) {
      setValidTill(batchInfo.valid_till);
    } else if (selectedBatchId) {
      const batch = batches.find(b => b.batch_id === selectedBatchId);
      const monthNum = MONTH_MAP[batch?.end_month?.toLowerCase() ?? ''];
      const year     = Number(batch?.end_year);

      if (monthNum && !isNaN(year) && year > 0) {
        // Day 0 of (monthNum + 1) = last day of end_month
        const lastDay = new Date(year, monthNum, 0);
        setValidTill(lastDay.toISOString().split('T')[0]); // "YYYY-MM-DD"
      } else {
        setValidTill('');
      }
    }
  }, [batchInfo, selectedBatchId, batches]);

  // Load batches
  useEffect(() => {
    (async () => {
      try {
        setBatchesLoading(true);
        const res = await apiClient.get('/v1/batches');
        if (res.data.success) {
          const active = res.data.data.filter((b: Batch) => b.is_active);
          setBatches(active);
          if (active.length > 0) setSelectedBatchId(active[0].batch_id);
        }
      } catch {
        setError('Failed to load batches');
      } finally {
        setBatchesLoading(false);
      }
    })();
  }, []);

  // Load students when batch changes
  useEffect(() => {
    if (!selectedBatchId) return;
    (async () => {
      try {
        setStudentsLoading(true); setError(null); setSelectedIds(new Set());
        const params = new URLSearchParams({ page: page.toString(), limit: '10', ...(search && { search }) });
        const res = await apiClient.get<ApiResponse>(`/v1/students/get-students-by-batch/${selectedBatchId}?${params}`);
        if (res.data.success) {
          setStudents(res.data.data);
          setPagination(res.data.pagination);
        }
      } catch (e: any) {
        setError(e.response?.data?.error || 'Failed to load students');
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    })();
  }, [selectedBatchId, page, search]);

  const batchOptions: DropdownOption[] = batches.map(b => ({
    label: `${b.batch_name} · ${b.batch_time}`,
    value: b.batch_id,
  }));

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.mobileNumber || '').includes(search)
  );

  const toggleSelect = (id: string) =>
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleAll = () =>
    selectedIds.size === filtered.length
      ? setSelectedIds(new Set())
      : setSelectedIds(new Set(filtered.map(s => s.student_id)));

  const handlePrint = (override?: Student[]) => {
    const pw = window.open('', '_blank');
    if (!pw) return;
    const cards = override ?? (selectedIds.size > 0
      ? filtered.filter(s => selectedIds.has(s.student_id))
      : filtered);
    pw.document.write(buildPrintHTML(cards, batchInfo, schoolName, schoolAddress, websiteUrl, validTill, branchId));
    pw.document.close();
  };

  // Shared styles
  const cardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '14px',
    boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
    border: '1px solid #E5E7EB',
  };

  const colStyle: React.CSSProperties = {
    color: '#9ca3af',
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  };

  const renderPagination = () => {
    const pages: (number | string)[] = [];
    const total = pagination.pages;

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (page >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', page, '...', total);
    }

    return pages.map((p, i) =>
      p === '...' ? (
        <span key={`e${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: '0.82rem' }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{
            minWidth: '34px', height: '34px', padding: '0 8px', borderRadius: '8px',
            fontSize: '0.83rem', fontWeight: page === p ? '700' : '500',
            background: page === p ? '#0e225d' : 'transparent',
            color: page === p ? '#fbb21f' : '#0e225d',
            border: page === p ? 'none' : '1px solid #E5E7EB',
            cursor: 'pointer', transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => { if (page !== p) e.currentTarget.style.background = 'rgba(14,34,93,0.06)'; }}
          onMouseLeave={(e) => { if (page !== p) e.currentTarget.style.background = 'transparent'; }}
        >
          {p}
        </button>
      )
    );
  };

  if (batchesLoading) return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: '400px', background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB'
    }}>
      <div style={{ textAlign: 'center' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ marginTop: '14px', color: '#0e225d', fontSize: '0.9rem', fontWeight: '500' }}>Loading...</p>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .idc-row:hover { background: rgba(14,34,93,0.03) !important; }
        .idc-row { transition: background 0.15s ease; }
        .idc-cb  { accent-color: #0e225d; width: 16px; height: 16px; cursor: pointer; }
        .idc-input::placeholder { color: #c4cdd8; }
        .idc-input:focus { outline: none; border-color: #0e225d !important; box-shadow: 0 0 0 3px rgba(14,34,93,0.08); }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0', margin: '0 auto' }}>

        {/* Batch Selector + Settings */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {/* Batch Dropdown */}
          <div style={{ ...cardStyle, padding: '14px 18px', flex: 2, minWidth: '260px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              <div style={{ width: '4px', height: '18px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
              <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d' }}>
                <i className="pi pi-th-large" style={{ fontSize: '0.8rem', marginRight: '6px' }} />
                Select Batch
              </span>
            </div>
            <GlobalDropdownField
              value={selectedBatchId}
              onChange={setSelectedBatchId}
              options={batchOptions}
              placeholder="Select a batch"
              icon="pi pi-users"
            />
          </div>

          {/* Settings Card */}
          <div style={{ ...cardStyle, padding: '14px 18px', flex: 3, minWidth: '280px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '4px', height: '18px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
                <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d' }}>
                  <i className="pi pi-cog" style={{ fontSize: '0.8rem', marginRight: '6px' }} />
                  Card Settings
                </span>
              </div>
              <button
                onClick={() => setShowSettings(v => !v)}
                style={{
                  padding: '5px 12px',
                  background: showSettings ? 'rgba(14,34,93,0.08)' : 'transparent',
                  border: '1px solid #E5E7EB', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', color: '#0e225d'
                }}
              >
                {showSettings ? 'Hide' : 'Edit'} Settings
              </button>
            </div>

            {showSettings ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {([
                  { label: 'School Name',              value: schoolName,    set: setSchoolName,    icon: 'pi-building'    },
                  { label: 'Branch Name',              value: schoolAddress, set: setSchoolAddress, icon: 'pi-map-marker'  },
                  { label: 'Website URL',              value: websiteUrl,    set: setWebsiteUrl,    icon: 'pi-globe'       },
                  { label: 'Valid Till (e.g. 2026-09-30)', value: validTill, set: setValidTill,     icon: 'pi-clock'       },
                ] as const).map(f => (
                  <div key={f.label}>
                    <label style={{
                      fontSize: '0.72rem', fontWeight: '600', color: '#9ca3af',
                      textTransform: 'uppercase', letterSpacing: '0.4px',
                      display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px'
                    }}>
                      <i className={`pi ${f.icon}`} style={{ fontSize: '0.72rem' }} /> {f.label}
                    </label>
                    <input
                      value={f.value}
                      onChange={e => (f.set as any)(e.target.value)}
                      className="idc-input"
                      style={{
                        width: '100%', padding: '8px 12px',
                        border: '1.5px solid #E5E7EB', borderRadius: '8px',
                        fontSize: '0.83rem', color: '#0e225d', fontWeight: '500',
                        fontFamily: 'inherit', transition: 'all 0.2s'
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {[
                  ['Institute', schoolName],
                  ['Branch',   schoolAddress],
                  ['URL',      websiteUrl],
                  ['Valid Till', formatDate(validTill || null)],
                ].map(([l, v]) => (
                  <div key={l} style={{ padding: '6px 12px', background: '#E8EEF7', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase' }}>{l}</div>
                    <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#0e225d', marginTop: '2px' }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.88rem', marginBottom: '20px' }} />
        )}

        {/* Student Table */}
        {selectedBatchId && (
          <div style={{ ...cardStyle, marginBottom: '20px', overflow: 'hidden' }}>
            <div style={{
              padding: '14px 18px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              gap: '12px', flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '4px', height: '22px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#0e225d' }}>
                  Students {students.length > 0 && <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af' }}>{students.length}</span>}
                </h2>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative' }}>
                  <i className="pi pi-search" style={{
                    position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                    fontSize: '0.8rem', color: '#9ca3af'
                  }} />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search students..."
                    className="idc-input"
                    style={{
                      paddingLeft: '32px', paddingRight: '12px', height: '36px',
                      border: '1.5px solid #E5E7EB', borderRadius: '8px',
                      fontSize: '0.82rem', color: '#0e225d', fontFamily: 'inherit',
                      width: '200px', transition: 'all 0.2s'
                    }}
                  />
                </div>
                <button
                  onClick={() => handlePrint()}
                  disabled={studentsLoading || filtered.length === 0}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '0 18px', height: '36px',
                    background: (studentsLoading || filtered.length === 0)
                      ? 'rgba(14,34,93,0.3)'
                      : 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                    border: 'none', borderRadius: '10px', color: 'white',
                    fontSize: '0.8rem', fontWeight: '700',
                    cursor: (studentsLoading || filtered.length === 0) ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 8px rgba(14,34,93,0.25)'
                  }}
                >
                  <i className="pi pi-print" style={{ fontSize: '0.8rem' }} />
                  {selectedIds.size > 0 ? `Print ${selectedIds.size} Selected` : 'Print All'}
                </button>
              </div>
            </div>

            {studentsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" animationDuration=".8s" />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                <div style={{
                  width: '56px', height: '56px', background: 'rgba(14,34,93,0.06)',
                  borderRadius: '14px', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', margin: '0 auto 12px'
                }}>
                  <i className="pi pi-users" style={{ fontSize: '1.6rem', color: '#0e225d', opacity: 0.3 }} />
                </div>
                <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>No students found</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...colStyle, padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', background: '#fafafa' }}>
                      <input
                        type="checkbox" className="idc-cb"
                        checked={selectedIds.size === filtered.length && filtered.length > 0}
                        onChange={toggleAll}
                      />
                    </th>
                    {['Student', 'Father', 'DOB', 'Contact', 'Blood', 'Admission', 'Preview'].map(h => (
                      <th key={h} style={{ ...colStyle, padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', background: '#fafafa' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(student => (
                    <tr key={student.student_id} className="idc-row">
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        <input
                          type="checkbox" className="idc-cb"
                          checked={selectedIds.has(student.student_id)}
                          onChange={() => toggleSelect(student.student_id)}
                        />
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ position: 'relative' }}>
                            <Avatar
                              image={student.profilePicture?.cdn_url || undefined}
                              icon={!student.profilePicture ? 'pi pi-user' : undefined}
                              size="normal" shape="circle"
                              style={{
                                width: '36px', height: '36px',
                                background: student.profilePicture ? 'transparent' : '#E8EEF7',
                                color: '#0e225d', border: '2px solid #E5E7EB'
                              }}
                            />
                            {student.is_registered && (
                              <div style={{
                                position: 'absolute', bottom: '-2px', right: '-2px',
                                width: '12px', height: '12px', background: '#16a34a',
                                borderRadius: '50%', border: '2px solid white'
                              }} />
                            )}
                          </div>
                          <div>
                            <div style={{ fontSize: '0.83rem', fontWeight: '600', color: '#0e225d' }}>{student.name}</div>
                            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{student.enrollment_id || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>
                        {student.fatherName || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>
                        {formatDOB(student.dateOfBirth)}
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>
                        {student.mobileNumber || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        {student.bloodGroup
                          ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 10px',
                              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                              borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', color: '#dc2626'
                            }}>
                              {student.bloodGroup}
                            </span>
                          )
                          : <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>—</span>
                        }
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>
                        {formatDate(student.date_of_admission)}
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        <button
                          onClick={() => setPreviewStudent(student)}
                          title="Preview"
                          style={{
                            width: '32px', height: '32px', background: 'transparent',
                            border: '1px solid transparent', borderRadius: '8px',
                            cursor: 'pointer', color: '#9ca3af', transition: 'all 0.2s',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(14,34,93,0.08)'; e.currentTarget.style.color = '#0e225d'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
                        >
                          <i className="pi pi-id-card" style={{ fontSize: '0.85rem' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!studentsLoading && filtered.length > 0 && (
              <div style={{
                padding: '12px 18px', borderTop: '1px solid #f3f4f6',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                  {selectedIds.size > 0
                    ? <><strong style={{ color: '#0e225d' }}>{selectedIds.size}</strong> selected of {filtered.length}</>
                    : <>{filtered.length} students — <span style={{ color: '#fbb21f', fontWeight: '600' }}>select rows to print specific cards</span></>
                  }
                </span>
                {selectedIds.size > 0 && (
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    style={{ fontSize: '0.75rem', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Clear selection
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {selectedBatchId && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: '10px', padding: '14px 18px', background: 'white',
            borderRadius: '14px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
            border: '1px solid #E5E7EB', flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
              Showing {((page - 1) * pagination.limit) + 1}–{Math.min(page * pagination.limit, pagination.total)} of {pagination.total} students
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              {[
                { icon: 'pi-angle-double-left',  action: () => setPage(1),                           disabled: page === 1              },
                { icon: 'pi-angle-left',         action: () => setPage(p => Math.max(1, p - 1)),     disabled: page === 1              },
              ].map((btn, i) => (
                <button key={i} onClick={btn.action} disabled={btn.disabled} style={{
                  width: '30px', height: '30px', background: 'transparent',
                  border: '1px solid #E5E7EB', borderRadius: '7px',
                  cursor: btn.disabled ? 'not-allowed' : 'pointer',
                  color: '#0e225d', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <i className={`pi ${btn.icon}`} style={{ fontSize: '0.7rem' }} />
                </button>
              ))}

              {renderPagination()}

              {[
                { icon: 'pi-angle-right',        action: () => setPage(p => Math.min(pagination.pages, p + 1)), disabled: page === pagination.pages },
                { icon: 'pi-angle-double-right', action: () => setPage(pagination.pages),                       disabled: page === pagination.pages },
              ].map((btn, i) => (
                <button key={i} onClick={btn.action} disabled={btn.disabled} style={{
                  width: '30px', height: '30px', background: 'transparent',
                  border: '1px solid #E5E7EB', borderRadius: '7px',
                  cursor: btn.disabled ? 'not-allowed' : 'pointer',
                  color: '#0e225d', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <i className={`pi ${btn.icon}`} style={{ fontSize: '0.7rem' }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {!selectedBatchId && batches.length > 0 && (
          <div style={{ ...cardStyle, padding: '60px 20px', textAlign: 'center' }}>
            <div style={{
              width: '64px', height: '64px', background: 'rgba(14,34,93,0.06)',
              borderRadius: '16px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 16px'
            }}>
              <i className="pi pi-id-card" style={{ fontSize: '1.8rem', color: '#0e225d', opacity: 0.3 }} />
            </div>
            <h3 style={{ margin: '0 0 8px 0', color: '#0e225d', fontSize: '1.2rem', fontWeight: '700' }}>Select a Batch</h3>
            <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>
              Choose a batch above to load students and generate ID cards
            </p>
          </div>
        )}

        {/* Preview Dialog */}
        {previewStudent && (
          <Dialog
            visible={!!previewStudent}
            onHide={() => setPreviewStudent(null)}
            style={{ width: 'auto' }}
            dismissableMask
            header={false}
            pt={{
              root: {
                style: {
                  borderRadius: '20px', overflow: 'hidden',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 20px 60px rgba(14,34,93,0.2)',
                  background: 'transparent'
                }
              },
              header: { style: { display: 'none' } },
              content: { style: { padding: '24px', background: '#E8EEF7', borderRadius: '20px' } },
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d' }}>
                  <i className="pi pi-id-card" style={{ marginRight: '6px' }} />
                  ID Card Preview
                </span>
                <button
                  onClick={() => setPreviewStudent(null)}
                  style={{
                    background: 'rgba(14,34,93,0.08)', border: 'none', borderRadius: '8px',
                    width: '32px', height: '32px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0e225d'
                  }}
                >
                  <i className="pi pi-times" style={{ fontSize: '0.8rem' }} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Front Side
                  </div>
                  <IdCardFront
                    student={previewStudent}
                    batchInfo={batchInfo}
                    schoolName={schoolName}
                    schoolAddress={schoolAddress}
                    websiteUrl={websiteUrl}
                    validTill={validTill}
                    branchId={branchId}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Back Side
                  </div>
                  <IdCardBack
                    student={previewStudent}
                    batchInfo={batchInfo}
                    schoolName={schoolName}
                    schoolAddress={schoolAddress}
                    websiteUrl={websiteUrl}
                    validTill={validTill}
                    branchId={branchId}
                  />
                </div>
              </div>

              <button
                onClick={() => {
                  const s = previewStudent;
                  setPreviewStudent(null);
                  handlePrint([s]);
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                  border: 'none', borderRadius: '10px', color: 'white',
                  fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(14,34,93,0.3)'
                }}
              >
                <i className="pi pi-print" style={{ fontSize: '0.85rem' }} />
                Print This Card
              </button>
            </div>
          </Dialog>
        )}
      </div>
    </>
  );
};

export default IdCardGenerator;